import styled from '@emotion/styled'
import {
    ApiImageDimensions,
    ApiShopperLandingPageLink,
    DEFAULT_PRODUCT_SORT_OPTION,
} from '@storefronts/common-types'
import { useKeenSlider } from 'keen-slider/react'
import type { KeenSliderInstance } from 'keen-slider'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { AnalyticsContext } from '../../../context/analyticsContext'
import { useEscapeToDismiss } from '../../../hooks/useEscapeToDismiss'
import {
    ACCENT_FILL_COLOR_VAR,
    ACCENT_TEXT_COLOR_VAR,
    CustomizationContext,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SECONDARY_CTA_FILL_COLOR_VAR,
    SECONDARY_CTA_TEXT_COLOR_VAR,
    SECONDARY_FONT_CSS_VAR,
} from '../../../context/CustomizationContextProvider'
import {
    PageContext,
    ActiveLPLinkOptions,
} from '../../../context/PageContextProvider'
import { QueryParamContext } from '../../../context/QueryParamContextProvider'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import { Constants } from '../../../style/styleConstants'
import {
    HORIZONTAL_SCROLL_CARD_WIDTH_PX,
    MAX_GRID_CARD_WIDTH_PX,
    MAX_GRID_COLUMNS,
    responsiveGridColumns,
} from '../content-sections/Shared'
import {
    inScrollingDesktop,
    scaledFontSize,
    ScrollingLayout,
    SECTION_CONTAINER_NAME,
} from '../../../style/scrollingLayout'
import { hoverFill } from '../../../style/interactiveStates'
import { getTypographyRoleStyle } from '../../../style/typography'
import { openExternalUrl } from '../../../utils/externalNavigation'
import {
    belowFoldImageProps,
    shouldDeprioritizeCardImage,
} from '../../../utils/getFirstLcpImageSectionIndex'
import { getReviewSummary } from '../../../utils/getReviewSummary'
import { imageUrlTransform } from '../../../utils/imageUrlTransform'
import {
    priceFormat,
    formatPriceWithoutSymbol,
    splitDollarsAndCents,
} from '../../../utils/priceFormat'
import {
    getDisplayData,
    resolveProductCardImageDimensions,
    resolveProductCardImageUrls,
} from '../../../utils/productDisplay'
import { CarouselChevron, ExternalLink } from '../../icons/Icons'
import {
    CurrencySymbol,
    DiscountBadge,
    ListPrice,
    ListPriceLabel,
    PriceCents,
    PriceDollars,
} from '../../shared/ProductCard'
import { CartIcon } from '../CartIcon'
import { ReviewsStars } from '../reviews/Reviews'
import { DrawerHeader } from './DrawerHeader'
import { useHeaderScrollVisibility } from '../../../hooks/useHeaderScrollVisibility'
import { FullScreenContainer } from './FullScreenContainer'
import {
    overlayModalCloseClearance,
    overlayModalPanel,
    overlayModalScrim,
} from '../../shared/overlayModal'
import { PoweredByFooter } from '../../shared/PoweredByFooter'
import { useDesktopModal } from '../../../hooks/useDesktopModal'
import { OverlayCloseButton } from '../../shared/OverlayCloseButton'
import { useColumnCount } from '../../../context/ProductLayoutContext'
import {
    ProductSortProvider,
    useProductSort,
} from '../../../context/ProductSortContext'
import { sortProducts } from '../../../utils/productSorting'
import { ProductControls } from '../../shared/ProductControls'
import {
    ProductFilterPills,
    useFilterState,
} from '../../shared/ProductFilterDialog'
import { AddToCartInlineCTA } from './AddToCart'
import type { ProductData } from '../products/ProductContent'
import {
    getNumberOfAvailableSwatchOptions,
    ProductGridColorSwatches,
} from '../../shared/variant-options/ProductGridColorSwatches'
import { useProductGridVariantSelection } from '../../shared/variant-options/useProductGridVariantSelection'
import { MIN_VISIBLE_REVIEW_COUNT } from '../../../constants'
import { PLP_GRID_KEEN_CLASS } from '../../../style/keenStyles'

export const ItemsGridEmptyState = styled.div`
    grid-column: 1 / -1;
    width: 100%;
    text-align: center;
    padding: 40px 20px;
    color: var(${PRIMARY_PAGE_TEXT_COLOR_VAR});
`

const PLPOverlayContainer = styled(FullScreenContainer)<{
    expanded: boolean
}>(({ expanded }) => ({
    overflowY: 'scroll',
    overflowX: 'hidden',
    background: expanded
        ? `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`
        : 'transparent',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
    [ScrollingLayout.DESKTOP_MEDIA]: {
        ...overlayModalPanel,
        // The panel is also the scroll box here; rounded corners still clip,
        // so only the axes need restating.
        overflowY: 'auto',
        overflowX: 'hidden',
    },
}))

const PLPFullscreenContainer = styled(FullScreenContainer)<{
    expanded: boolean
}>(({ expanded }) => ({
    pointerEvents: 'none',
    background: expanded
        ? `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`
        : 'transparent',
    [ScrollingLayout.DESKTOP_MEDIA]: overlayModalScrim,
}))

const OpaqueContainer = styled.div({
    position: 'relative',
    minHeight: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    pointerEvents: 'auto',
    // The overlay covers the window, so the products inside it hold the page's
    // content column. Declaring the container is what lets the grid read that
    // width instead of the window's.
    containerType: 'inline-size',
    containerName: SECTION_CONTAINER_NAME,
})

/**
 * The header bar spans the overlay the way the page's own header does, so the
 * products below it — not the bar — are what holds the content column.
 */
const PLPBody = styled.div<{ modal: boolean }>(({ modal }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: ScrollingLayout.CONTENT_MAX_WIDTH,
    marginInline: 'auto',
    ...(modal ? overlayModalCloseClearance : null),
}))

const NewDesignGridInnerContainer = styled.div<{ columnCount: number }>(
    (props) => ({
        display: 'grid',
        gridTemplateColumns: `repeat(${props.columnCount}, minmax(0, 1fr))`,
        // The overlay is as wide as the window here, so the column count comes
        // from that width rather than from the shopper's toggle, exactly as it
        // does for a grid section on the page.
        ...responsiveGridColumns,
        gridAutoRows: 'auto',
        width: '100%',
        background: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
        paddingTop: 8,
        paddingBottom: 0,
        paddingLeft: 16,
        paddingRight: 16,
        gap: '16px 12px',
        boxSizing: 'border-box',
    }),
)

const VerticalProductGridLinkContainer = styled.div({
    boxSizing: 'border-box',
    padding: '0',
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    cursor: 'pointer',
    display: 'grid',
    gridRow: 'span 3',
    gridTemplateRows: 'subgrid',
    rowGap: 8,
    minWidth: 0,
    width: '100%',
})

// Defined here (not Emotion styled) because Emotion's inline <style data-emotion>
// tag for this component surfaces as visible CSS text inside the first
// horizontal-grid card without JS. Class rules live in globals.css.
const HorizontalProductGridLinkContainer: React.FC<{
    children: React.ReactNode
}> = ({ children }) => <div className="lp-horizontal-grid-link">{children}</div>

const NewDesignDiscountBadge = styled.div({
    position: 'absolute',
    top: '-4px',
    left: '4px',
    margin: '8px 0px 0px 8px',
    ...inScrollingDesktop({
        left: 'auto',
        right: '4px',
        margin: '8px 8px 0px 0px',
    }),
    color: `var(${ACCENT_TEXT_COLOR_VAR})`,
    background: `var(${ACCENT_FILL_COLOR_VAR})`,
    backdropFilter: 'blur(16px)',
    borderRadius: '4px',
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 5px',
    ...getTypographyRoleStyle('badgeText', {
        fontWeight: '100',
    }),
    fontSize: scaledFontSize(12),
    padding: '0 4px',
})

// alignSelf: stretch + flex column lets the swatches container use
// marginTop: auto to sit at the bottom of the shared subgrid text row so
// swatches line up across sibling cards regardless of the title / price /
// compare-price stack above them.
const VerticalProductGridTextContentContainer = styled.div({
    paddingLeft: 0,
    paddingRight: 0,
    gridRow: 2,
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    width: '100%',
})

const HorizontalProductGridTextContentContainer = styled.div({
    paddingLeft: 0,
    paddingRight: 0,
    gridRow: 2,
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    width: '100%',
})

// Anchors the swatches to the bottom of the text row so they align across
// sibling cards. Empty on cards without swatches (no wrapper is rendered).
const SwatchAnchorBottom = styled.div({
    marginTop: 'auto',
    width: '100%',
    minWidth: 0,
})

const VerticalProductGridButtonContainer = styled.div({
    alignSelf: 'start',
    gridRow: 3,
    marginBottom: 8,
    minWidth: 0,
    width: '100%',
})

const HorizontalProductGridButtonContainer = styled.div({
    gridRow: 3,
    alignSelf: 'end',
    marginBottom: 8,
    minWidth: 0,
    width: '100%',
})

const Row = styled.div({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 8,
    fontSize: scaledFontSize(16),
    fontWeight: 500,
    minWidth: 0,
    width: '100%',
    wordBreak: 'break-word',
})

const VisitBrandButton = styled.div({
    gridColumn: '1 / -1',
    width: '100%',
    height: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    marginTop: 8,
    marginBottom: 40,
    border: `1px solid var(${SECONDARY_CTA_TEXT_COLOR_VAR})`,
    borderRadius: 8,
    cursor: 'pointer',
    ...getTypographyRoleStyle('buttonText', {
        fontWeight: 400,
    }),
    fontSize: scaledFontSize(16),
    backgroundColor: `var(${SECONDARY_CTA_FILL_COLOR_VAR})`,
    color: `var(${SECONDARY_CTA_TEXT_COLOR_VAR})`,
})

const VerticalProductGridImageContainer = styled.div({
    backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    width: '100%',
    minWidth: 0,
    minHeight: 0,
    gridRow: 1,
    alignSelf: 'stretch',
})

const HorizontalProductGridImageContainer = styled.div({
    backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    width: '100%',
    minWidth: 0,
    minHeight: 0,
    gridRow: 1,
    alignSelf: 'stretch',
})

/**
 * Only in the wide layout, and only while the card is hovered: on a phone the
 * swipe is the affordance, and arrows parked over a small image cover more of
 * it than they are worth.
 */
const CardNavArrow = styled.button<{ side: 'left' | 'right' }>(({ side }) => ({
    display: 'none',
    ...inScrollingDesktop({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        [side]: 6,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 28,
        height: 28,
        padding: 0,
        border: 'none',
        borderRadius: '50%',
        cursor: 'pointer',
        zIndex: 2,
        // Product shots are mostly cut out on white, so the disc needs an edge
        // of its own and a dark chevron to read against one.
        background: '#ffffff',
        color: '#1a1a1a',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.1)',
        opacity: 0,
        ...hoverFill('#ffffff', '#1a1a1a'),
        transition:
            'opacity 0.15s ease-out, background-color 0.15s ease-out, color 0.15s ease-out',
        '&:disabled': { display: 'none' },
    }),
}))

const SwiperWrapper = styled.div({
    position: 'relative',
    isolation: 'isolate',
    width: '100%',
    minWidth: 0,
    height: '100%',
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    [`&:hover ${CardNavArrow}, &:focus-within ${CardNavArrow}`]: {
        opacity: 1,
    },
})

const PLP_IMAGE_PLACEHOLDER_DIMENSION = 1000

const PLPImagePlaceholder = styled.div({
    width: '100%',
    aspectRatio: '1 / 1',
    backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    borderRadius: '0.2rem',
})

const plpGridImageStyle = {
    width: '100%' as const,
    height: 'auto' as const,
    display: 'block' as const,
    borderRadius: '0.2rem',
}

const DotsRow = styled.div({
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 6,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    pointerEvents: 'none',
    zIndex: 1,
})

const Dot = styled.div<{ active: boolean }>(({ active }) => ({
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: active ? '#ffffff' : 'rgba(255, 255, 255, 0.55)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)',
    transition: 'transform 0.18s ease-out, background-color 0.18s ease-out',
    transform: active ? 'scale(1.15)' : 'scale(1)',
}))

const PLP_IMAGE_SIZES_SINGLE_COLUMN = '(max-width: 734px) 100vw, 470px'
// Two columns of a phone screen, or the widest card the responsive grid paints.
const PLP_IMAGE_SIZES_MULTI_COLUMN = `(max-width: ${Constants.EXPANDED_BREAKPOINT}px) 50vw, ${MAX_GRID_CARD_WIDTH_PX}px`
const PLP_IMAGE_SIZES_HORIZONTAL_SCROLL = `${HORIZONTAL_SCROLL_CARD_WIDTH_PX}px`
const MAX_DOTS = 5

const getPLPImageSizes = (
    columnCount: number,
    isHorizontalScroll?: boolean,
) => {
    if (isHorizontalScroll) {
        return PLP_IMAGE_SIZES_HORIZONTAL_SCROLL
    }
    return columnCount === 1
        ? PLP_IMAGE_SIZES_SINGLE_COLUMN
        : PLP_IMAGE_SIZES_MULTI_COLUMN
}

type PLPItemImageSwiperProps = {
    imageUrls: string[]
    imageDimensions: (ApiImageDimensions | undefined)[]
    altText: string
    columnCount: number
    isHorizontalScroll?: boolean
    badge?: React.ReactNode
    onTap: () => void
    deprioritizeImage?: boolean
}

const PLPItemImageCarousel: React.FC<PLPItemImageSwiperProps> = ({
    imageUrls,
    imageDimensions,
    altText,
    columnCount,
    isHorizontalScroll = false,
    badge,
    onTap,
    deprioritizeImage = true,
}) => {
    const { isGridExpanded } = useContext(PageContext)
    const sizes = getPLPImageSizes(columnCount, isHorizontalScroll)

    const [currentSlide, setCurrentSlide] = useState(0)
    const [loadedCount, setLoadedCount] = useState(1)
    const draggedRef = useRef(false)

    const instanceRef = useRef<KeenSliderInstance | null>(null)

    const [sliderRef] = useKeenSlider<HTMLDivElement>({
        initial: 0,
        slides: { perView: 1, spacing: 0 },
        renderMode: 'performance',
        drag: true,
        dragSpeed: 0.8,
        loop: false,
        created(slider) {
            instanceRef.current = slider
        },
        destroyed() {
            instanceRef.current = null
        },
        dragStarted(slider) {
            setLoadedCount((n) => Math.max(n, slider.track.details.rel + 2))
        },
        dragChecked() {
            draggedRef.current = true
        },
        slideChanged(slider) {
            const rel = slider.track.details.rel
            setCurrentSlide(rel)
            setLoadedCount((n) => Math.max(n, rel + 2))
        },
    })

    // Recalculate slide dimensions when the column count changes or the drawer
    // opens. By including isGridExpanded, the effect re-runs after display:none
    // is removed, at which point offsetWidth reflects the real container width.
    useEffect(() => {
        const container = instanceRef.current?.container
        if (!container || container.offsetWidth === 0) return
        instanceRef.current?.update()
    }, [columnCount, isGridExpanded])

    const handlePointerDown = () => {
        draggedRef.current = false
    }

    const handleClick = (e: React.MouseEvent) => {
        if (draggedRef.current) {
            draggedRef.current = false
            e.preventDefault()
            e.stopPropagation()
            return
        }
        onTap()
    }

    // The wrapper turns a click into opening the product, so stepping the
    // carousel has to stop the event before it gets there.
    const step = (delta: number) => (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        instanceRef.current?.moveToIdx(currentSlide + delta)
    }

    const dotCount = Math.min(imageUrls.length, MAX_DOTS)
    const activeDotIdx =
        imageUrls.length <= MAX_DOTS
            ? currentSlide
            : Math.round(
                  (currentSlide / (imageUrls.length - 1)) * (dotCount - 1),
              )

    return (
        <SwiperWrapper onPointerDown={handlePointerDown} onClick={handleClick}>
            <div
                ref={sliderRef}
                className={`keen-slider ${PLP_GRID_KEEN_CLASS}`}
            >
                {imageUrls.map((url, idx) => (
                    <div key={idx} className="keen-slider__slide">
                        {idx < loadedCount ? (
                            <Image
                                src={url}
                                alt={idx === 0 ? altText : ''}
                                width={
                                    imageDimensions[idx]?.width ??
                                    PLP_IMAGE_PLACEHOLDER_DIMENSION
                                }
                                height={
                                    imageDimensions[idx]?.height ??
                                    PLP_IMAGE_PLACEHOLDER_DIMENSION
                                }
                                sizes={sizes}
                                style={plpGridImageStyle}
                                {...belowFoldImageProps(deprioritizeImage)}
                            />
                        ) : (
                            <PLPImagePlaceholder aria-hidden />
                        )}
                    </div>
                ))}
            </div>
            {badge}
            <CardNavArrow
                side="left"
                aria-label="Previous image"
                disabled={currentSlide === 0}
                onClick={step(-1)}
            >
                <CarouselChevron width={16} direction="left" />
            </CardNavArrow>
            <CardNavArrow
                side="right"
                aria-label="Next image"
                disabled={currentSlide >= imageUrls.length - 1}
                onClick={step(1)}
            >
                <CarouselChevron width={16} direction="right" />
            </CardNavArrow>
            <DotsRow aria-hidden>
                {Array.from({ length: dotCount }, (_, idx) => (
                    <Dot key={idx} active={idx === activeDotIdx} />
                ))}
            </DotsRow>
        </SwiperWrapper>
    )
}

// Split so single-image cards never mount a keen-slider instance.
const PLPItemImageSwiper: React.FC<PLPItemImageSwiperProps> = (props) => {
    if (props.imageUrls.length <= 1) {
        const src = props.imageUrls[0]
        const sizes = getPLPImageSizes(
            props.columnCount,
            props.isHorizontalScroll,
        )
        return (
            <SwiperWrapper onClick={props.onTap}>
                {src ? (
                    <Image
                        src={src}
                        alt={props.altText}
                        width={
                            props.imageDimensions[0]?.width ??
                            PLP_IMAGE_PLACEHOLDER_DIMENSION
                        }
                        height={
                            props.imageDimensions[0]?.height ??
                            PLP_IMAGE_PLACEHOLDER_DIMENSION
                        }
                        sizes={sizes}
                        style={plpGridImageStyle}
                        {...belowFoldImageProps(
                            props.deprioritizeImage ?? true,
                        )}
                    />
                ) : (
                    <PLPImagePlaceholder aria-label={props.altText} />
                )}
                {props.badge}
            </SwiperWrapper>
        )
    }
    // Remount when the image set changes: keen-slider doesn't observe DOM child
    // count, and a fresh instance also resets the slide index and loaded count.
    return (
        <PLPItemImageCarousel
            key={JSON.stringify(props.imageUrls)}
            {...props}
        />
    )
}

const PLPItem: React.FC<{
    link: ApiShopperLandingPageLink
    onItemClick: (
        link: ApiShopperLandingPageLink,
        options?: ActiveLPLinkOptions,
    ) => void
    columnCount: number
    isHorizontalScroll?: boolean
    deprioritizeImage?: boolean
    // eslint-disable-next-line complexity
}> = ({
    link: lpl,
    onItemClick,
    columnCount,
    isHorizontalScroll = false,
    deprioritizeImage = true,
}) => {
    const { landingPage } = useContext(ViddyLPContext)
    const { reviewStarColorOverride, accentFillColor } =
        useContext(CustomizationContext)

    /** Only for swatch UI + `preferAvailable` in the grid hook; pricing/image/title still use `activeVariant` when this is false. */
    const hasSwatchOption = getNumberOfAvailableSwatchOptions(lpl) > 1

    const {
        selectedOptions,
        handleOptionSelect,
        activeVariant,
        setActiveVariant,
        reviewsRef,
        price,
        comparePrice,
        discountPercentage,
        selectedSellingPlanId,
        setSelectedSellingPlanId,
    } = useProductGridVariantSelection({
        lpLink: lpl,
        showSwatches: hasSwatchOption,
    })

    const rowProductData = useMemo(
        (): ProductData => ({
            activeVariant,
            setActiveVariant,
            reviewsRef,
            price,
            comparePrice,
            discountPercentage,
            selectedSellingPlanId,
            setSelectedSellingPlanId,
        }),
        [
            activeVariant,
            setActiveVariant,
            reviewsRef,
            price,
            comparePrice,
            discountPercentage,
            selectedSellingPlanId,
            setSelectedSellingPlanId,
        ],
    )

    // Variant-aware display data for virtual products (title, reviews, etc.)
    // `activeVariant` always comes from useProductGridVariantSelection / useProductData (swatches on or off).
    const displayData = useMemo(
        () => getDisplayData(lpl, activeVariant?.shopifyProductId ?? null),
        [lpl, activeVariant],
    )

    const sourceImageUrls = useMemo(
        () => resolveProductCardImageUrls(activeVariant, lpl, displayData),
        [activeVariant, lpl, displayData],
    )
    const imageUrls = useMemo(
        () => sourceImageUrls.map((url) => imageUrlTransform(url)),
        [sourceImageUrls],
    )
    const imageDimensions = useMemo(
        () =>
            sourceImageUrls.map((url) =>
                resolveProductCardImageDimensions(url, activeVariant, lpl),
            ),
        [sourceImageUrls, activeVariant, lpl],
    )

    const displayTitle = displayData.title ?? lpl.title
    const altText = activeVariant
        ? activeVariant.title || displayTitle || 'Product Image'
        : displayTitle || 'Product Image'

    const { dollars, cents } = useMemo(() => {
        const stringPrice = formatPriceWithoutSymbol(price)
        return splitDollarsAndCents(stringPrice)
    }, [price])

    const { reviewRating, reviewCount } = getReviewSummary(landingPage, {
        ...lpl,
        reviews: displayData.reviews ?? lpl.reviews,
    })

    const starColorForText = reviewStarColorOverride || accentFillColor

    const openPdpFromRow = () =>
        onItemClick(lpl, {
            initialVariantShopifyId: activeVariant?.shopifyId ?? undefined,
        })

    const LinkContainer = isHorizontalScroll
        ? HorizontalProductGridLinkContainer
        : VerticalProductGridLinkContainer
    const ImageContainerComponent = isHorizontalScroll
        ? HorizontalProductGridImageContainer
        : VerticalProductGridImageContainer
    const TextContentContainerComponent = isHorizontalScroll
        ? HorizontalProductGridTextContentContainer
        : VerticalProductGridTextContentContainer
    const ButtonContainerComponent = isHorizontalScroll
        ? HorizontalProductGridButtonContainer
        : VerticalProductGridButtonContainer

    return (
        <LinkContainer>
            <ImageContainerComponent>
                <PLPItemImageSwiper
                    imageUrls={imageUrls}
                    imageDimensions={imageDimensions}
                    altText={altText}
                    columnCount={columnCount}
                    isHorizontalScroll={isHorizontalScroll}
                    deprioritizeImage={deprioritizeImage}
                    badge={
                        lpl.badgeText && (
                            <NewDesignDiscountBadge data-testid="discount-badge">
                                {lpl.badgeText}
                            </NewDesignDiscountBadge>
                        )
                    }
                    onTap={openPdpFromRow}
                />
            </ImageContainerComponent>
            <TextContentContainerComponent>
                <Row
                    onClick={openPdpFromRow}
                    style={{
                        ...getTypographyRoleStyle('productTitle', {
                            fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
                            fontWeight: 300,
                            lineHeight: 1.2,
                        }),
                        fontSize: scaledFontSize(16),
                        marginBottom: 10,
                    }}
                >
                    {displayTitle}
                </Row>
                {reviewCount != null &&
                    reviewRating != null &&
                    reviewCount > MIN_VISIBLE_REVIEW_COUNT && (
                        <Row
                            onClick={openPdpFromRow}
                            style={{
                                marginTop: -2,
                                marginBottom: 10,
                            }}
                        >
                            <ReviewsStars
                                rating={reviewRating}
                                count={reviewCount}
                                showRating={false}
                                // Collection grid rating: slightly smaller than PDP
                                starSize={14}
                                fontSize={12}
                                starColor={starColorForText}
                                textColor={`var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`}
                            />
                        </Row>
                    )}
                <Row
                    onClick={openPdpFromRow}
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: 0,
                    }}
                >
                    {discountPercentage != null && discountPercentage > 0 && (
                        <DiscountBadge trailing>
                            -{discountPercentage}%
                        </DiscountBadge>
                    )}
                    <CurrencySymbol
                        style={{ fontSize: scaledFontSize(12), marginTop: 2 }}
                    >
                        {landingPage?.client?.currencySymbol || '$'}
                    </CurrencySymbol>
                    <PriceDollars
                        style={{
                            ...getTypographyRoleStyle('price', {
                                fontWeight: 500,
                                lineHeight: 1,
                            }),
                            fontSize: scaledFontSize(20),
                        }}
                    >
                        {dollars}
                    </PriceDollars>
                    <PriceCents
                        style={{ fontSize: scaledFontSize(12), marginTop: 2 }}
                    >
                        {cents}
                    </PriceCents>
                </Row>
                {/* Only show compare price if there's an actual discount (price comparison) */}
                {comparePrice != null && comparePrice > price && (
                    <div style={{ marginTop: 5 }}>
                        <ListPriceLabel>
                            List Price:{' '}
                            <ListPrice>
                                {priceFormat(
                                    comparePrice,
                                    landingPage?.client?.currencySymbol,
                                )}
                            </ListPrice>
                        </ListPriceLabel>
                    </div>
                )}
                {hasSwatchOption && (
                    <SwatchAnchorBottom>
                        <ProductGridColorSwatches
                            lpLink={lpl}
                            selectedOptions={selectedOptions}
                            onOptionSelect={handleOptionSelect}
                        />
                    </SwatchAnchorBottom>
                )}
            </TextContentContainerComponent>
            <ButtonContainerComponent>
                <AddToCartInlineCTA
                    link={lpl}
                    rowProductData={rowProductData}
                />
            </ButtonContainerComponent>
        </LinkContainer>
    )
}

export const ItemsGrid: React.FC<{
    landingPageLinks: ApiShopperLandingPageLink[]
    columnCount: number
    isHorizontalScroll?: boolean
    isBelowFold?: boolean
}> = ({
    landingPageLinks,
    columnCount,
    isHorizontalScroll = false,
    isBelowFold = true,
}) => {
    const { setActiveLPLink } = useContext(PageContext)
    const { Analytics } = useContext(AnalyticsContext)
    const _onLinkContainerClick = (
        lpl: ApiShopperLandingPageLink,
        options?: ActiveLPLinkOptions,
    ) => {
        Analytics.TRACK_LANDING_PAGE_MENU_ITEM_CLICK(lpl)
        setActiveLPLink(lpl, options)
    }

    return (
        <>
            {landingPageLinks.map((lpl, cardIndex) => {
                // Exclude Dr. Brandt free gift add on
                // TODO: Remove this when we have a better way to exclude links
                if (lpl.linkHash == '7c45dc0e-227a-4be2-86dc-0249e02352bf') {
                    return null
                }
                return (
                    <PLPItem
                        link={lpl}
                        onItemClick={_onLinkContainerClick}
                        key={lpl.linkHash}
                        columnCount={columnCount}
                        isHorizontalScroll={isHorizontalScroll}
                        deprioritizeImage={shouldDeprioritizeCardImage({
                            isBelowFold,
                            cardIndex,
                            // Column count is CSS-only, so assume a full
                            // row for first-row LCP.
                            firstVisibleCount: isHorizontalScroll
                                ? 1
                                : MAX_GRID_COLUMNS,
                        })}
                    />
                )
            })}
        </>
    )
}

type PLPItemsGridProps = {
    visibleProducts?: string[]
    products?: ApiShopperLandingPageLink[]
    isBelowFold?: boolean
}

export const PLPItemsGrid: React.FC<
    PLPItemsGridProps & { isHorizontalScroll?: boolean }
> = ({
    visibleProducts,
    products,
    isHorizontalScroll = false,
    isBelowFold = true,
}) => {
    const { landingPage } = useContext(ViddyLPContext)
    const columnCount = useColumnCount()
    const allProducts = products || landingPage?.landingPageLinks || []
    let filteredProducts = allProducts
    if (visibleProducts) {
        filteredProducts = visibleProducts
            .map((hash) => allProducts.find((link) => link.linkHash === hash))
            .filter((link): link is ApiShopperLandingPageLink => Boolean(link))
    }

    return (
        <ItemsGrid
            landingPageLinks={filteredProducts}
            columnCount={columnCount}
            isHorizontalScroll={isHorizontalScroll}
            isBelowFold={isBelowFold}
        />
    )
}

export const PLPContent: React.FC<{
    inline: boolean
    isHeaderVisible?: boolean
}> = ({ isHeaderVisible }) => {
    return (
        <ProductSortProvider defaultSort={DEFAULT_PRODUCT_SORT_OPTION}>
            <PLPContentInner isHeaderVisible={isHeaderVisible} />
        </ProductSortProvider>
    )
}

const PLPContentInner: React.FC<{
    isHeaderVisible?: boolean
}> = ({ isHeaderVisible }) => {
    const { landingPage } = useContext(ViddyLPContext)
    const desktopModal = useDesktopModal()
    const { displayPoweredByFooter } = useContext(CustomizationContext)
    const { Analytics } = useContext(AnalyticsContext)
    const { setIsGridExpanded, collectionOverrideLinks } =
        useContext(PageContext)
    const { HIDE_CART_OVERRIDE } = useContext(QueryParamContext)
    const columnCount = useColumnCount()
    const { sortOption } = useProductSort()
    const router = useRouter()
    const storefrontProducts = landingPage.landingPageLinks ?? []
    const filterState = useFilterState()

    const baseLinks = collectionOverrideLinks ?? storefrontProducts

    // Sort products based on selected sort option
    const landingPageLinks = useMemo(() => {
        return sortProducts(baseLinks, sortOption, landingPage.discount || null)
    }, [baseLinks, sortOption, landingPage.discount])

    const filteredProducts = useMemo(() => {
        return filterState.getFilteredProducts(
            landingPageLinks,
            landingPage.discount,
        )
    }, [
        landingPageLinks,
        filterState.getFilteredProducts,
        landingPage.discount,
    ])

    const onVisitBrandClick = () => {
        const clientMainDomain = landingPage.client.mainDomain
        if (clientMainDomain) {
            Analytics.TRACK_PLP_VIEW_ON_SITE_CLICK(landingPage.client.name)
            openExternalUrl(clientMainDomain, router.query, {
                utmContent: 'product_listing_page',
            })
        }
    }

    const _onNavGridClose = () => {
        // PageContext clears any collection override when the grid closes.
        setIsGridExpanded(false)
    }

    return (
        <OpaqueContainer>
            {desktopModal ? (
                <OverlayCloseButton onClick={_onNavGridClose} />
            ) : (
                <DrawerHeader
                    onClose={_onNavGridClose}
                    rightSlot={!HIDE_CART_OVERRIDE ? <CartIcon /> : undefined}
                    bannerText={landingPage.bannerText}
                    bannerBenefits={landingPage.bannerBenefits}
                    isHeaderVisible={isHeaderVisible}
                />
            )}
            <PLPBody modal={desktopModal}>
                <div style={{ padding: '0 16px', marginTop: 12 }}>
                    <ProductControls
                        products={landingPageLinks}
                        filterState={filterState}
                    />
                </div>
                <div style={{ padding: '0 16px' }}>
                    <ProductFilterPills
                        activeFilters={filterState.selectedFilters}
                        removeFilter={filterState.removeFilter}
                        priceRange={filterState.priceRange}
                        removePriceFilter={filterState.removePriceFilter}
                        currencySymbol={landingPage.client?.currencySymbol}
                    />
                </div>
                <NewDesignGridInnerContainer columnCount={columnCount}>
                    {landingPageLinks.length > 0 &&
                        filteredProducts.length === 0 && (
                            <ItemsGridEmptyState>
                                No products match your filters. Try adjusting
                                your selections.
                            </ItemsGridEmptyState>
                        )}
                    <ItemsGrid
                        landingPageLinks={filteredProducts}
                        columnCount={columnCount}
                    />
                    {landingPage.client.mainDomain && (
                        <VisitBrandButton onClick={onVisitBrandClick}>
                            <span style={{ marginRight: 5 }}>
                                See more on{' '}
                                {landingPage.client.displayName || 'site'}
                            </span>
                            <ExternalLink width={20} />
                        </VisitBrandButton>
                    )}
                    <div style={{ gridColumn: '1 / -1', width: '100%' }}>
                        <PoweredByFooter
                            displayPoweredByFooter={displayPoweredByFooter}
                        />
                    </div>
                </NewDesignGridInnerContainer>
            </PLPBody>
        </OpaqueContainer>
    )
}

export const PLPDrawer: React.FC<{ zIndex?: number }> = ({
    zIndex = 'unset',
}) => {
    const { isGridExpanded, setIsGridExpanded, activeLPLink, isCartOpen } =
        useContext(PageContext)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const isHeaderVisible = useHeaderScrollVisibility(scrollContainerRef)

    const close = useCallback(
        () => setIsGridExpanded(false),
        [setIsGridExpanded],
    )
    // A product or the cart opened from the list sits above it, and Escape
    // belongs to whichever of those is showing.
    const isTopmost = isGridExpanded && activeLPLink === null && !isCartOpen
    useEscapeToDismiss(isTopmost, close)

    return (
        <PLPFullscreenContainer
            expanded={isGridExpanded}
            style={{ zIndex, display: isGridExpanded ? 'flex' : 'none' }}
            onClick={(event) => {
                if (event.target === event.currentTarget) close()
            }}
        >
            <PLPOverlayContainer
                ref={scrollContainerRef}
                expanded={isGridExpanded}
            >
                <PLPContent inline={false} isHeaderVisible={isHeaderVisible} />
            </PLPOverlayContainer>
        </PLPFullscreenContainer>
    )
}
